import { useReducer, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useLocalNotificationBanner, useTranslation } from 'Shared/util/custom-hooks';
import PropTypes from 'prop-types';
import makeComponentTrashable from 'trashable-react';
import useTranslationContext from 'Contexts/translation-context';
import useCustomerContext from 'Contexts/customer-context';
import { fetchKycQuestions, saveKycAnswers } from 'Shared/util/web-api';
import { formatDateAndTime } from 'Shared/util/formatters';
import Page from 'Shared/components/page/page';
import Button from 'Shared/components/button';
import { CustomerStatus } from 'Shared/util/types';
import TextFormCard from './form-cards/text-form-card';
import RadioFormCard from './form-cards/radio-form-card';
import { validateQuestion, getValidQuestions, createAnswerList } from './kyc-utils';
import styles from './kyc-page.css';


const NOTIFICATION_BANNER_SUCCESS_ID = 'KYC_PAGE_SUCCESS';
const NOTIFICATION_BANNER_ERROR_ID = 'KYC_PAGE_ERROR';

const mapLanguageCode = (languageCode) => {
  switch (languageCode) {
    case 'sv': return 'sv-SE';
    case 'en': return 'en-US';
    case 'fi': return 'en-US'; // 210527: Currently finnish is not supported
    default: return 'en-US';
  }
};

const renderFormCard = (
  question,
  validQuestions,
  disabled,
  handleFormCardAnswerChanged,
  languageCode,
) => {
  const { id, kind } = question;

  const valid = validQuestions.includes(question.id);

  switch (kind.toLowerCase()) {
    case 'text': {
      const { answer } = question;

      return (
        <TextFormCard
          key={id}
          languageCode={languageCode}
          questionId={id}
          valid={valid}
          disabled={disabled}
          headerTexts={question.questionTexts}
          answer={answer}
          onAnswerChange={handleFormCardAnswerChanged}
          className={styles.formCardContainer}
        />
      );
    }
    case 'radio': {
      const {
        answer,
        numberOfOptions,
        optionsTexts,
        childComponent,
      } = question;

      return (
        <RadioFormCard
          key={id}
          languageCode={languageCode}
          questionId={id}
          valid={valid}
          disabled={disabled}
          headerTexts={question.questionTexts}
          answer={answer}
          onAnswerChange={handleFormCardAnswerChanged}
          numberOfOptions={numberOfOptions}
          optionsTexts={optionsTexts}
          childComponent={childComponent}
          className={styles.formCardContainer}
        />
      );
    }
    default:
      throw new Error(`Unsupported question kind '${question && question.kind}'`);
  }
};


const initialState = {
  pageLoading: true,
  allowEdit: false,
  allowSubmit: false,
  questions: [],
  validQuestions: [],
  isSavingAnswers: false,
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'QUESTIONS_FETCH_FINISHED': {
      const allowSubmit = (action.payload.allowEdit && action.payload.validQuestions.length === action.payload.questions.length);
      return {
        ...state,
        pageLoading: false,
        allowEdit: action.payload.allowEdit,
        allowSubmit,
        questions: action.payload.questions,
        validQuestions: action.payload.validQuestions,
      };
    }
    case 'ANSWER_CHANGED': {
      // eslint-disable-next-line no-nested-ternary
      const questions = state.questions.map((a) => (a.id === action.payload.questionId
        ? !a.childComponent
          ? { ...a, answer: action.payload.answer }
          : { ...a, answer: action.payload.answer, childComponent: { ...a.childComponent, answer: action.payload.childComponentAnswer } }
        : a
      ));
      let validQuestions = state.validQuestions.filter((id) => id !== action.payload.questionId);
      if (action.payload.valid) {
        validQuestions = [...validQuestions, action.payload.questionId];
      }
      const allowSubmit = (state.allowEdit && validQuestions.length === questions.length);
      return {
        ...state,
        allowSubmit,
        questions,
        validQuestions,
      };
    }
    case 'SAVING_ANSWERS_STARTED':
      return {
        ...state,
        allowEdit: false,
        isSavingAnswers: true,
      };
    case 'SAVING_ANSWERS_FINISHED':
      return {
        ...state,
        allowEdit: action.payload.allowEdit,
        allowSubmit: action.payload.allowEdit,
        isSavingAnswers: false,
      };
    case 'SAVING_ANSWERS_FAILED':
      return {
        ...state,
        allowEdit: true,
        isSavingAnswers: false,
      };
    default:
      throw new Error(`Unexpected action '${action}'`);
  }
};

const KycPage = ({
  registerPromise,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const history = useHistory();
  const { translationData } = useTranslationContext();
  const { updateCustomerStatus } = useCustomerContext();
  const t = useTranslation(x => x.kyc);
  const {
    NotificationBannerType,
    addLocalNotificationBanner,
    removeLocalNotificationBanner,
  } = useLocalNotificationBanner();

  const {
    pageLoading,
    allowEdit,
    allowSubmit,
    questions,
    validQuestions,
    isSavingAnswers,
  } = state;

  useEffect(() => {
    registerPromise(fetchKycQuestions())
      .then((result) => {
        const {
          allowEdit: allowEditResult,
          questions: questionsResult,
          lastAnsweredDate: lastAnsweredDateResult,
        } = result;

        if (lastAnsweredDateResult) {
          addLocalNotificationBanner((tnb) => tnb.format(tnb.last_kyc_answered_date, { date: formatDateAndTime(lastAnsweredDateResult) }), NotificationBannerType.Success, false, NOTIFICATION_BANNER_SUCCESS_ID);
        }

        const initialValidQuestions = getValidQuestions(questionsResult);
        dispatch({
          type: 'QUESTIONS_FETCH_FINISHED',
          payload: {
            allowEdit: allowEditResult,
            questions: questionsResult,
            validQuestions: initialValidQuestions,
          },
        });
      })
      .catch(() => addLocalNotificationBanner((tnb) => tnb.a_technical_error_occurred, NotificationBannerType.Error, false, NOTIFICATION_BANNER_ERROR_ID));
  }, []);


  const handleAnswerChanged = (questionId, kind, answer, childComponentEnabledOn = null, childComponentKind = null, childComponentAnswer = null) => {
    const valid = validateQuestion(kind, answer, childComponentEnabledOn, childComponentKind, childComponentAnswer);
    dispatch({
      type: 'ANSWER_CHANGED',
      payload: {
        questionId, valid, answer, childComponentAnswer,
      },
    });
  };

  const handleSaveAnswers = () => {
    removeLocalNotificationBanner('KYC_PAGE_ERROR');

    dispatch({ type: 'SAVING_ANSWERS_STARTED' });

    const answers = createAnswerList(questions);
    registerPromise(saveKycAnswers(answers))
      .then((customerStatusResult) => {
        const {
          status,
          lastUpdatedKycQuestion,
        } = customerStatusResult;

        updateCustomerStatus(customerStatusResult);
        addLocalNotificationBanner((tnb) => tnb.format(tnb.last_kyc_answered_date, { date: formatDateAndTime(lastUpdatedKycQuestion) }), NotificationBannerType.Success, false, NOTIFICATION_BANNER_SUCCESS_ID);
        dispatch({ type: 'SAVING_ANSWERS_FINISHED', payload: { allowEdit: (status === CustomerStatus.ONBOARDING_IN_PROGRESS) } });
      })
      .catch(() => {
        dispatch({ type: 'SAVING_ANSWERS_FAILED' });
        addLocalNotificationBanner((tnb) => tnb.a_technical_error_occurred_while_saving_kyc, NotificationBannerType.Error, false, NOTIFICATION_BANNER_ERROR_ID);
      });
  };

  const handleGoBackClick = () => history.push('/company-settings');

  return (
    <Page
      narrowWidth
      title={t.know_your_customer}
      loading={pageLoading}
    >
      {questions.map((question) => renderFormCard(question, validQuestions, !allowEdit, handleAnswerChanged, mapLanguageCode(translationData.languageCode)))}
      <div className={styles.buttonsContainer}>
        <Button
          buttonType="action-secondary"
          text={t.go_back}
          onClick={handleGoBackClick}
          attributes={{ disabled: isSavingAnswers }}
          className={styles.goBackButton}
        />
        <Button
          buttonType="action-primary"
          text={t.submit_your_answers}
          onClick={handleSaveAnswers}
          isLoading={isSavingAnswers}
          attributes={{ disabled: !allowSubmit }}
        />
      </div>
    </Page>
  );
};

KycPage.propTypes = {
  registerPromise: PropTypes.func.isRequired,
};

export default makeComponentTrashable(KycPage);
